/**
 * Settings → Document Templates (config-style).
 *
 * Kept separate from the legacy DocumentTemplatesSettings (markdown body).
 * This is the file-upload + targeting + renewal UI that Pilot and
 * Sentinel read from.
 */
import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  MinusCircle,
  Pencil,
  Plus,
  Save,
  Trash,
  Trash2,
  Upload,
} from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import {
  createConfigTemplate,
  deleteConfigTemplate,
  listConfigTemplates,
  purgeConfigTemplate,
  replaceTemplateFile,
  updateConfigTemplate,
  type DocumentTemplateConfig,
  type TemplateCategory,
  type TemplateConfigCreatePayload,
} from "@/api/documentTemplates";
import { FormBuilder } from "@/components/forms/FormBuilder";
import { FormPresetPicker } from "@/components/forms/FormPresetPicker";
import type { FormField } from "@/components/forms/types";

/**
 * A template counts as "ready for onboarding" when:
 *   * file mode → a file has been uploaded (file_url present), OR
 *   * form mode → a non-empty form_schema is saved.
 *
 * This mirrors the backend gate logic: Pilot will warn before
 * assigning if the file/form contents are missing, but a saved
 * digital form with fields IS the deliverable, so it should not be
 * flagged as "No file".
 */
function isTemplateReady(t: DocumentTemplateConfig): boolean {
  if ((t.template_type ?? "file") === "form") {
    const schema = t.form_schema as unknown as unknown[] | null;
    return Array.isArray(schema) && schema.length > 0;
  }
  return !!t.file_url;
}

const CATEGORIES: { value: TemplateCategory; label: string }[] = [
  { value: "onboarding", label: "Onboarding" },
  { value: "compliance", label: "Compliance" },
  { value: "policy", label: "Policy" },
  { value: "benefits", label: "Benefits" },
  { value: "tax", label: "Tax" },
  { value: "other", label: "Other" },
];

const EMPTY: TemplateConfigCreatePayload = {
  name: "",
  description: "",
  category: "onboarding",
  is_required: true,
  requires_signature: true,
  deadline_days: 5,
  renewal_interval: null,
  renewal_days: null,
  applies_to_all: true,
  applies_to_departments: null,
  applies_to_employment_types: null,
  sort_order: 0,
  template_type: "file",
  form_schema: null,
};

export function DocumentConfigSettings() {
  const { addToast } = useToast();
  const [templates, setTemplates] = useState<DocumentTemplateConfig[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DocumentTemplateConfig | null>(null);
  const [form, setForm] = useState<TemplateConfigCreatePayload>(EMPTY);
  const [file, setFile] = useState<File | null>(null);
  const [filter, setFilter] = useState<string>("all");
  // When the admin first switches to "Digital form" with no schema,
  // we show the preset picker. Setting this true via "Replace with
  // preset…" lets them swap a starting point even after picking one.
  const [showPresetPicker, setShowPresetPicker] = useState(false);
  // Permanent-delete confirmation modal state. Holds the template
  // we're about to nuke + the in-use assignment count surfaced by
  // the 409 response (so we can show "force delete X assignments").
  const [purgeTarget, setPurgeTarget] = useState<DocumentTemplateConfig | null>(
    null,
  );
  const [purgeAssignmentCount, setPurgeAssignmentCount] = useState<number | null>(
    null,
  );
  const [purgeBusy, setPurgeBusy] = useState(false);

  const load = async () => {
    try {
      const data = await listConfigTemplates({ includeInactive: true });
      setTemplates(data);
    } catch {
      addToast("error", "Failed to load templates");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setFile(null);
    setShowPresetPicker(false);
    setOpen(true);
  };

  const openEdit = (t: DocumentTemplateConfig) => {
    setEditing(t);
    setForm({
      name: t.name,
      description: t.description ?? "",
      category: (t.category ?? "onboarding") as TemplateCategory,
      is_required: t.is_required,
      requires_signature: t.requires_signature,
      deadline_days: t.deadline_days,
      renewal_interval: t.renewal_interval,
      renewal_days: t.renewal_days,
      applies_to_all: t.applies_to_all,
      applies_to_departments: t.applies_to_departments,
      applies_to_employment_types: t.applies_to_employment_types,
      sort_order: t.sort_order,
      template_type: t.template_type ?? "file",
      form_schema: (t.form_schema ?? null) as TemplateConfigCreatePayload["form_schema"],
    });
    setFile(null);
    setShowPresetPicker(false);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      addToast("error", "Name is required");
      return;
    }
    try {
      if (editing) {
        await updateConfigTemplate(editing.id, form);
        if (file) await replaceTemplateFile(editing.id, file);
      } else {
        await createConfigTemplate(form, file);
      }
      setOpen(false);
      addToast("success", "Template saved");
      await load();
    } catch {
      addToast("error", "Failed to save");
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteConfigTemplate(id);
      addToast("success", "Template archived");
      await load();
    } catch {
      addToast("error", "Failed to archive");
    }
  };

  const openPurge = (t: DocumentTemplateConfig) => {
    setPurgeTarget(t);
    setPurgeAssignmentCount(null);
  };

  const confirmPurge = async (force: boolean) => {
    if (!purgeTarget) return;
    setPurgeBusy(true);
    try {
      await purgeConfigTemplate(purgeTarget.id, { force });
      addToast("success", "Template permanently deleted");
      setPurgeTarget(null);
      setPurgeAssignmentCount(null);
      await load();
    } catch (err: unknown) {
      // Surface the 409 "still in use" path so the admin can choose
      // to force-delete (which cascades) or cancel and clean up
      // assignments first.
      const detail = (err as { response?: { status?: number; data?: { detail?: { assignment_count?: number } } } })
        ?.response;
      if (detail?.status === 409) {
        const count = detail.data?.detail?.assignment_count ?? 0;
        setPurgeAssignmentCount(count);
      } else {
        addToast("error", "Failed to delete template");
      }
    } finally {
      setPurgeBusy(false);
    }
  };

  const visible = useMemo(
    () =>
      filter === "all"
        ? templates
        : templates.filter((t) => (t.category || "other") === filter),
    [templates, filter],
  );

  // Onboarding-readiness summary at the top of the page. Counted off
  // ALL active templates (filter doesn't apply — this is a global
  // health metric, not a per-category one). Inactive templates are
  // ignored entirely so an archived row doesn't drag the metric down.
  const readinessStats = useMemo(() => {
    const active = templates.filter((t) => t.is_active);
    const ready = active.filter((t) => isTemplateReady(t));
    const empty = active.filter((t) => !isTemplateReady(t));
    return {
      activeCount: active.length,
      readyCount: ready.length,
      emptyCount: empty.length,
      emptyTemplates: empty,
    };
  }, [templates]);

  return (
    <div className="space-y-6">
      {/* Onboarding readiness — visible at the top so HR knows whether
          Pilot will warn them at Start Onboarding time. */}
      {templates.length > 0 && (
        <Card>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              {readinessStats.emptyCount === 0 &&
              readinessStats.activeCount > 0 ? (
                <CheckCircle2 className="h-5 w-5 text-success shrink-0" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-warning shrink-0" />
              )}
              <div>
                <p className="text-sm font-medium text-text-primary">
                  {readinessStats.readyCount} of{" "}
                  {readinessStats.activeCount} document template
                  {readinessStats.activeCount === 1 ? "" : "s"} ready for
                  onboarding
                </p>
                {readinessStats.emptyCount > 0 && (
                  <p className="text-xs text-text-muted mt-0.5">
                    Some templates are missing documents. Pilot will
                    warn admins before sending these to new hires.
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}
      <Card>
        <CardHeader>
          <CardTitle>Document Templates (new)</CardTitle>
          <div className="flex items-center gap-2">
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              options={[
                { value: "all", label: "All categories" },
                ...CATEGORIES.map((c) => ({
                  value: c.value,
                  label: c.label,
                })),
              ]}
            />
            <Button size="sm" onClick={openNew}>
              <Plus className="h-4 w-4" />
              New template
            </Button>
          </div>
        </CardHeader>

        {visible.length === 0 ? (
          <div className="py-8 text-center text-sm text-text-muted">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
            No templates in this category yet.
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {visible.map((t) => {
              // One-look readiness state per row — green check when the
              // template is active and has a file, yellow when active
              // but empty, gray when archived/inactive. Mirrors the
              // backend's gate logic so HR can predict what Pilot will
              // do without clicking around.
              const status: "ready" | "no_file" | "inactive" = !t.is_active
                ? "inactive"
                : isTemplateReady(t)
                  ? "ready"
                  : "no_file";
              const isForm = (t.template_type ?? "file") === "form";
              return (
              <div
                key={t.id}
                className="py-3 flex items-center gap-3 justify-between"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {status === "ready" && (
                      <span
                        className="inline-flex items-center gap-1 text-xs text-success"
                        title={
                          isForm
                            ? "Form configured — ready for onboarding"
                            : "File uploaded — ready for onboarding"
                        }
                      >
                        <CheckCircle2 className="h-4 w-4" />
                      </span>
                    )}
                    {status === "no_file" && (
                      <span
                        className="inline-flex items-center gap-1 text-xs text-warning"
                        title={
                          isForm
                            ? "No fields configured — Pilot will warn before assigning"
                            : "No file uploaded — Pilot will warn before assigning"
                        }
                      >
                        <AlertTriangle className="h-4 w-4" />
                      </span>
                    )}
                    {status === "inactive" && (
                      <span
                        className="inline-flex items-center gap-1 text-xs text-text-muted"
                        title="Inactive — not used by Pilot"
                      >
                        <MinusCircle className="h-4 w-4" />
                      </span>
                    )}
                    <p className="font-medium text-text-primary truncate">
                      {t.name}
                    </p>
                    {status === "ready" && (
                      <Badge variant="success">
                        {isForm ? "Form ready" : "Ready"}
                      </Badge>
                    )}
                    {status === "no_file" && (
                      <Badge variant="warning">
                        {isForm ? "No fields" : "No file"}
                      </Badge>
                    )}
                    {status === "inactive" && (
                      <Badge variant="default">Inactive</Badge>
                    )}
                    {t.category && (
                      <Badge variant="default">{t.category}</Badge>
                    )}
                    {t.requires_signature && (
                      <Badge variant="info">Signature</Badge>
                    )}
                    {t.is_required ? (
                      <Badge variant="warning">Required</Badge>
                    ) : (
                      <Badge variant="default">Optional</Badge>
                    )}
                    {t.renewal_interval && (
                      <Badge variant="default">
                        Renews {t.renewal_interval}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-text-muted mt-0.5 truncate">
                    {t.description || "No description"}
                    {t.file_name ? ` · ${t.file_name}` : ""}
                    {` · ${t.deadline_days}d deadline`}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEdit(t)}
                  title="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                {t.is_active ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(t.id)}
                    title="Archive"
                  >
                    <Trash2 className="h-4 w-4 text-danger" />
                  </Button>
                ) : (
                  // Already archived → offer permanent delete. Two-
                  // step flow: this just opens the confirm modal; the
                  // actual destructive call is gated behind that.
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openPurge(t)}
                    title="Delete permanently"
                  >
                    <Trash className="h-4 w-4 text-danger" />
                  </Button>
                )}
              </div>
              );
            })}
          </div>
        )}
      </Card>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit template" : "New template"}
        size="lg"
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Employee Handbook"
            />
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">
                Category
              </label>
              <Select
                value={form.category}
                onChange={(e) =>
                  setForm({
                    ...form,
                    category: e.target.value as TemplateCategory,
                  })
                }
                options={CATEGORIES.map((c) => ({
                  value: c.value,
                  label: c.label,
                }))}
              />
            </div>
          </div>

          <Input
            label="Description"
            value={form.description ?? ""}
            onChange={(e) =>
              setForm({ ...form, description: e.target.value })
            }
            placeholder="Optional — shown in the employee's to-do queue"
          />

          {/* Template type selector. ``file`` keeps the legacy
              PDF-upload flow; ``form`` swaps in a form builder + the
              employee-facing renderer. */}
          <div>
            <p className="text-xs font-medium text-text-muted mb-2">
              Template type
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(
                [
                  {
                    value: "file" as const,
                    label: "File upload",
                    hint: "Upload a PDF or DOCX. Employees view + sign.",
                  },
                  {
                    value: "form" as const,
                    label: "Digital form",
                    hint: "Build a fillable form. Employees submit values; we generate a PDF.",
                  },
                ]
              ).map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer transition-colors ${
                    (form.template_type ?? "file") === opt.value
                      ? "border-accent bg-accent/5"
                      : "border-border hover:bg-surface-hover"
                  }`}
                >
                  <input
                    type="radio"
                    name="template_type"
                    className="mt-1 accent-accent"
                    checked={(form.template_type ?? "file") === opt.value}
                    onChange={() =>
                      setForm({
                        ...form,
                        template_type: opt.value,
                        // Initialize an empty form schema when switching
                        // INTO form mode so the builder has something to
                        // mutate. Switching to file mode wipes the
                        // schema so it doesn't ride along on save.
                        form_schema:
                          opt.value === "form"
                            ? form.form_schema ?? []
                            : null,
                      })
                    }
                  />
                  <div>
                    <div className="text-sm font-medium text-text-primary">
                      {opt.label}
                    </div>
                    <div className="text-xs text-text-muted mt-0.5">
                      {opt.hint}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* File upload — only for template_type='file'. */}
          {(form.template_type ?? "file") === "file" && (
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">
                Template file (PDF / DOCX)
              </label>
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border cursor-pointer hover:bg-surface-hover">
                <Upload className="h-4 w-4" />
                <span className="text-sm text-text-muted">
                  {file
                    ? file.name
                    : editing?.file_name
                      ? `Current: ${editing.file_name} (upload to replace)`
                      : "Choose file"}
                </span>
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </label>
            </div>
          )}

          {/* Form builder — only for template_type='form'.
              First-time entry to form mode (or empty schema) shows the
              preset picker so admins land on a sensible starting
              point. Once a preset (or "Start blank") is chosen, the
              FormBuilder takes over with a "Replace with preset…"
              escape hatch in case they want to swap. */}
          {form.template_type === "form" && (
            <div className="rounded-md border border-border/40 bg-background/50 px-3 py-3">
              {showPresetPicker ||
              !form.form_schema ||
              (form.form_schema as unknown as FormField[]).length === 0 ? (
                <FormPresetPicker
                  onPick={(schema, presetMeta) => {
                    setForm((prev) => {
                      // Only auto-fill empty parent fields so we never
                      // clobber what the admin already typed in (name,
                      // category, etc.). Defaults from the preset only
                      // win where the parent is still at its EMPTY
                      // baseline.
                      const next: TemplateConfigCreatePayload = {
                        ...prev,
                        form_schema:
                          schema as unknown as TemplateConfigCreatePayload["form_schema"],
                      };
                      if (presetMeta) {
                        if (!prev.name.trim()) next.name = presetMeta.name;
                        if (!prev.description?.trim()) {
                          next.description = presetMeta.description;
                        }
                        // Category prefill only when still on the
                        // EMPTY baseline (onboarding) — otherwise the
                        // admin has explicitly chosen something.
                        if (prev.category === EMPTY.category) {
                          next.category = presetMeta.category as typeof prev.category;
                        }
                        if (presetMeta.default_required !== undefined) {
                          next.is_required = presetMeta.default_required;
                        }
                        if (presetMeta.default_requires_signature !== undefined) {
                          next.requires_signature =
                            presetMeta.default_requires_signature;
                        }
                        if (presetMeta.default_deadline_days) {
                          next.deadline_days = presetMeta.default_deadline_days;
                        }
                      }
                      return next;
                    });
                    setShowPresetPicker(false);
                  }}
                  // Cancel only meaningful when we got here via the
                  // "Replace with preset…" escape hatch (i.e. a schema
                  // already exists). For the first-time empty case,
                  // there's nothing to cancel back to.
                  onCancel={
                    showPresetPicker
                      ? () => setShowPresetPicker(false)
                      : undefined
                  }
                />
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-text-muted">
                      Form fields
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPresetPicker(true)}
                    >
                      Replace with preset…
                    </Button>
                  </div>
                  <FormBuilder
                    schema={
                      (form.form_schema ?? []) as unknown as Parameters<
                        typeof FormBuilder
                      >[0]["schema"]
                    }
                    onChange={(next) =>
                      setForm({
                        ...form,
                        form_schema: next as unknown as TemplateConfigCreatePayload["form_schema"],
                      })
                    }
                  />
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <Input
              label="Deadline (days)"
              type="number"
              value={String(form.deadline_days)}
              onChange={(e) =>
                setForm({
                  ...form,
                  deadline_days: Number(e.target.value || 5),
                })
              }
            />
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">
                Renewal
              </label>
              <Select
                value={form.renewal_interval ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    renewal_interval:
                      (e.target.value || null) as typeof form.renewal_interval,
                  })
                }
                options={[
                  { value: "", label: "One-time" },
                  { value: "annual", label: "Annual" },
                  { value: "biannual", label: "Every 2 years" },
                  { value: "quarterly", label: "Quarterly" },
                  { value: "custom", label: "Custom (days)" },
                ]}
              />
            </div>
            {form.renewal_interval === "custom" && (
              <Input
                label="Renewal (days)"
                type="number"
                value={form.renewal_days ? String(form.renewal_days) : ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    renewal_days: e.target.value
                      ? Number(e.target.value)
                      : null,
                  })
                }
              />
            )}
          </div>

          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="rounded border-border text-accent"
                checked={form.requires_signature}
                onChange={(e) =>
                  setForm({ ...form, requires_signature: e.target.checked })
                }
              />
              Requires signature
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="rounded border-border text-accent"
                checked={form.is_required}
                onChange={(e) =>
                  setForm({ ...form, is_required: e.target.checked })
                }
              />
              Required
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="rounded border-border text-accent"
                checked={form.applies_to_all}
                onChange={(e) =>
                  setForm({ ...form, applies_to_all: e.target.checked })
                }
              />
              Applies to everyone
            </label>
          </div>

          {!form.applies_to_all && (
            <p className="text-xs text-text-muted">
              Targeting by department / employment type — configure in the
              employee profile or via the bulk-assign modal for now. Full
              targeting UI lands in the next release.
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save}>
              <Save className="h-4 w-4" />
              Save
            </Button>
          </div>
        </div>
      </Modal>

      {/* Permanent-delete confirmation. Two-step destructive flow:
          * First click → backend refuses if any assignments exist (409).
          * If 409, we surface the count and offer force-delete which
            cascades to assignments. If no assignments, the first
            confirm click does the deletion. */}
      <Modal
        isOpen={!!purgeTarget}
        onClose={() => {
          if (purgeBusy) return;
          setPurgeTarget(null);
          setPurgeAssignmentCount(null);
        }}
        title="Delete template permanently?"
        size="md"
      >
        {purgeTarget && (
          <div className="space-y-4">
            <div className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-text-primary">
              <p className="font-medium">{purgeTarget.name}</p>
              <p className="text-xs text-text-muted mt-0.5">
                This action cannot be undone. The template will be
                removed from your organization.
              </p>
            </div>

            {purgeAssignmentCount !== null && purgeAssignmentCount > 0 ? (
              <div className="rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-sm">
                <p className="font-medium text-text-primary">
                  This template is still in use.
                </p>
                <p className="text-xs text-text-muted mt-1">
                  {purgeAssignmentCount} employee assignment
                  {purgeAssignmentCount === 1 ? "" : "s"} reference
                  this template (including any signed copies or
                  submitted form data). Choosing &ldquo;Delete
                  everything&rdquo; will also remove those records.
                </p>
              </div>
            ) : (
              <p className="text-sm text-text-muted">
                Are you sure you want to permanently delete this
                template?
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
              <Button
                variant="ghost"
                onClick={() => {
                  setPurgeTarget(null);
                  setPurgeAssignmentCount(null);
                }}
                disabled={purgeBusy}
              >
                Cancel
              </Button>
              {purgeAssignmentCount !== null && purgeAssignmentCount > 0 ? (
                <Button
                  variant="danger"
                  onClick={() => confirmPurge(true)}
                  disabled={purgeBusy}
                >
                  <Trash className="h-4 w-4" />
                  Delete everything
                </Button>
              ) : (
                <Button
                  variant="danger"
                  onClick={() => confirmPurge(false)}
                  disabled={purgeBusy}
                >
                  <Trash className="h-4 w-4" />
                  Delete permanently
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
